import { useState, useEffect } from "react";
import { Sparkles, Loader2, Check, Infinity as InfinityIcon, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import api from "../../lib/api";

export default function CareerUnlockModal({ open, onOpenChange }) {
  const [plans, setPlans] = useState(null);
  const [loading, setLoading] = useState(null);

  useEffect(() => {
    if (!open) return;
    api.get("/career/plans").then(({ data }) => setPlans(data)).catch(() => toast.error("Couldn't load plans"));
  }, [open]);

  const priceFor = (bucket) => (plans?.[bucket] || []).find((p) => p.currency === "sgd");
  const sym = "S$";

  const checkout = async (lookup_key) => {
    setLoading(lookup_key);
    try {
      const { data } = await api.post("/career/checkout", { lookup_key, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      setLoading(null);
      toast.error(e?.response?.data?.detail || "Couldn't start checkout");
    }
  };

  const lifetime = priceFor("lifetime");
  const monthly = priceFor("monthly");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-surface border-line text-ink sm:max-w-lg" data-testid="career-unlock-modal">
        <DialogHeader>
          <div className="w-12 h-12 rounded-xl bg-brand-soft border border-brand/30 flex items-center justify-center mb-2">
            <Sparkles className="w-6 h-6 text-brand" />
          </div>
          <DialogTitle className="font-display text-xl">Unlock Career Journey</DialogTitle>
          <DialogDescription className="text-body">
            Your personal AI career coach across every stage — Diagnose, Target, Gap, Build, Connect, Learn, Interview, Offer and Transition. The core résumé tools stay free forever.
          </DialogDescription>
        </DialogHeader>

        <p className="text-sm text-body text-center" data-testid="career-currency-note">Prices in Singapore dollars (SGD)</p>

        {!plans ? (
          <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 text-brand animate-spin" /></div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-3 mt-1">
            {/* Lifetime */}
            <div className="rounded-xl border border-brand/40 bg-brand-soft p-5 flex flex-col">
              <div className="flex items-center gap-2 text-brand mb-1"><InfinityIcon className="w-4 h-4" /><span className="text-xs font-semibold uppercase tracking-wide">Lifetime</span></div>
              <div className="font-display text-3xl font-extrabold">{sym}{lifetime?.amount}</div>
              <div className="text-xs text-faint mb-3">one-time · yours forever</div>
              <ul className="text-sm text-body space-y-1.5 mb-4 flex-1">
                <li className="flex gap-2"><Check className="w-4 h-4 text-brand shrink-0" /> All 9 coaching stages</li>
                <li className="flex gap-2"><Check className="w-4 h-4 text-brand shrink-0" /> Pay once, no subscription</li>
              </ul>
              <button
                data-testid="career-buy-lifetime"
                onClick={() => lifetime && checkout(lifetime.lookup_key)}
                disabled={!lifetime || loading}
                className="w-full gi-btn gi-btn-primary"
              >
                {loading === lifetime?.lookup_key ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Unlock forever
              </button>
            </div>
            {/* Monthly */}
            <div className="rounded-xl border border-line bg-canvas p-5 flex flex-col">
              <div className="flex items-center gap-2 text-body mb-1"><CalendarClock className="w-4 h-4" /><span className="text-xs font-semibold uppercase tracking-wide">Monthly</span></div>
              <div className="font-display text-3xl font-extrabold">{sym}{monthly?.amount}<span className="text-base text-faint font-normal">/mo</span></div>
              <div className="text-xs text-faint mb-3">cancel anytime</div>
              <ul className="text-sm text-body space-y-1.5 mb-4 flex-1">
                <li className="flex gap-2"><Check className="w-4 h-4 text-brand shrink-0" /> All 9 coaching stages</li>
                <li className="flex gap-2"><Check className="w-4 h-4 text-brand shrink-0" /> Lower upfront cost</li>
              </ul>
              <button
                data-testid="career-buy-monthly"
                onClick={() => monthly && checkout(monthly.lookup_key)}
                disabled={!monthly || loading}
                className="w-full gi-btn gi-btn-secondary"
              >
                {loading === monthly?.lookup_key ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Subscribe
              </button>
            </div>
          </div>
        )}
        <p className="text-center text-xs text-faint mt-1">Secure payment via Stripe · Test card 4242 4242 4242 4242</p>
      </DialogContent>
    </Dialog>
  );
}
